import { Controller } from "@hotwired/stimulus"
import I18n from "../../i18n"

const t = (key) => I18n.t(`erp.sales.printing_price_grids.show.${key}`)

export default class extends Controller {
  static targets = ["cell"]
  static values = { updateUrl: String, updateExtraUrl: String }

  editCell(event) {
    const cell = event.currentTarget
    if (cell.querySelector("input")) return

    const currentValue = cell.dataset.priceCents || ""
    const input = document.createElement("input")
    input.type = "number"
    input.step = "1"
    input.value = currentValue
    input.className = "w-20 px-1 py-0.5 text-right text-sm border border-black rounded bg-erp-input"

    const originalText = cell.textContent
    cell.textContent = ""
    cell.appendChild(input)
    input.focus()
    input.select()

    input.addEventListener("blur", () => {
      const newValue = input.value.trim()
      cell.textContent = ""

      if (newValue === "") {
        const span = document.createElement("span")
        span.className = "text-black/40 italic"
        span.textContent = t("on_quote")
        cell.appendChild(span)
      } else {
        cell.textContent = (parseInt(newValue) / 100).toFixed(2) + "€"
      }

      if (newValue !== currentValue) {
        cell.dataset.priceCents = newValue
        this.#saveEntry(cell.dataset.entryId, newValue, cell)
      }
    })

    input.addEventListener("keydown", (e) => {
      if (e.key === "Enter") input.blur()
      if (e.key === "Escape") { cell.textContent = originalText }
    })
  }

  editExtra(event) {
    const cell = event.currentTarget
    if (cell.querySelector("input")) return

    const priceType = cell.dataset.priceType
    const currentValue = cell.dataset.value || ""
    const input = document.createElement("input")
    input.type = "number"
    input.step = priceType === "percentage" ? "0.01" : "1"
    input.value = currentValue
    input.className = "w-20 px-1 py-0.5 text-right text-sm border border-black rounded bg-erp-input"

    const originalText = cell.textContent
    cell.textContent = ""
    cell.appendChild(input)
    input.focus()
    input.select()

    input.addEventListener("blur", () => {
      const newValue = input.value.trim()
      cell.textContent = ""

      if (newValue === "") {
        const span = document.createElement("span")
        span.className = "text-black/40 italic"
        span.textContent = t("on_quote")
        cell.appendChild(span)
      } else if (priceType === "percentage") {
        cell.textContent = "+" + parseFloat(newValue) + "%"
      } else {
        cell.textContent = (parseInt(newValue) / 100).toFixed(2) + "€"
      }

      if (newValue !== currentValue) {
        cell.dataset.value = newValue
        this.#saveExtra(cell.dataset.extraId, newValue, cell)
      }
    })

    input.addEventListener("keydown", (e) => {
      if (e.key === "Enter") input.blur()
      if (e.key === "Escape") { cell.textContent = originalText }
    })
  }

  get priceTypeLabels() {
    return {
      per_piece: t("per_piece"),
      fixed: t("fixed"),
      percentage: t("percentage")
    }
  }

  editExtraType(event) {
    const cell = event.currentTarget
    if (cell.querySelector("select")) return

    const currentType = cell.dataset.priceType
    const select = document.createElement("select")
    select.className = "px-1 py-0.5 text-sm border border-black rounded bg-erp-input"

    for (const [value, label] of Object.entries(this.priceTypeLabels)) {
      const option = document.createElement("option")
      option.value = value
      option.textContent = label
      if (value === currentType) option.selected = true
      select.appendChild(option)
    }

    const hint = document.createElement("p")
    hint.className = "text-xs text-black/40 mt-1"
    hint.textContent = t("price_will_reset")

    const originalText = cell.textContent
    cell.textContent = ""
    cell.appendChild(select)
    cell.appendChild(hint)
    select.focus()

    const commit = () => {
      const newType = select.value
      cell.textContent = this.priceTypeLabels[newType]

      if (newType !== currentType) {
        cell.dataset.priceType = newType

        // Reset the sibling price cell since old value is meaningless for new type
        const row = cell.closest("tr")
        const priceCell = row.querySelector("[data-price-type][data-extra-id]")
        if (priceCell) {
          priceCell.dataset.priceType = newType
          priceCell.dataset.value = ""
          priceCell.textContent = ""
          const span = document.createElement("span")
          span.className = "text-black/40 italic"
          span.textContent = t("on_quote")
          priceCell.appendChild(span)
        }

        this.#saveExtraType(cell.dataset.extraId, newType, cell)
      }
    }

    select.addEventListener("change", commit)
    select.addEventListener("blur", () => {
      if (cell.querySelector("select")) commit()
    })
  }

  async #saveExtraType(extraId, priceType, cell) {
    cell.classList.add("bg-amber-100")

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    const body = new FormData()
    body.append("_method", "patch")
    body.append("extra_id", extraId)
    body.append("price_type", priceType)

    try {
      const response = await fetch(this.updateExtraUrlValue, {
        method: "POST",
        headers: { "X-CSRF-Token": csrfToken, "Accept": "text/html" },
        body
      })
      this.#flashCell(cell, response.ok)
    } catch {
      this.#flashCell(cell, false)
    }
  }

  async #saveEntry(entryId, priceCents, cell) {
    cell.classList.add("bg-amber-100")

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    const body = new FormData()
    body.append("_method", "patch")
    body.append(`entries[${entryId}][price_cents]`, priceCents)

    try {
      const response = await fetch(this.updateUrlValue, {
        method: "POST",
        headers: { "X-CSRF-Token": csrfToken, "Accept": "text/html" },
        body
      })
      this.#flashCell(cell, response.ok)
    } catch {
      this.#flashCell(cell, false)
    }
  }

  async #saveExtra(extraId, value, cell) {
    cell.classList.add("bg-amber-100")

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    const body = new FormData()
    body.append("_method", "patch")
    body.append("extra_id", extraId)
    body.append("value", value)

    try {
      const response = await fetch(this.updateExtraUrlValue, {
        method: "POST",
        headers: { "X-CSRF-Token": csrfToken, "Accept": "text/html" },
        body
      })
      this.#flashCell(cell, response.ok)
    } catch {
      this.#flashCell(cell, false)
    }
  }

  #flashCell(cell, success) {
    cell.classList.remove("bg-amber-100")
    if (success) {
      cell.classList.add("bg-green-100")
      setTimeout(() => cell.classList.remove("bg-green-100"), 800)
    } else {
      cell.classList.add("bg-red-100")
      cell.title = I18n.t("erp.sales.js.inline_edit.save_error")
    }
  }
}
